import { Stack } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import type { CSSObjectWithLabel, GroupBase, StylesConfig } from 'react-select-modern';

import type { Campaign } from '@feathr/blackbox';
import type { ISelectOption } from '@feathr/components';
import { Alert, Button, Modal, showError, toast, zIndex } from '@feathr/components';
import CampaignSummary from '@feathr/extender/App/EventsPage/CampaignSummary';
import { useLocalUrl, useStore } from '@feathr/extender/state';

import EventSelect from '../EventSelect';

interface ICampaignMoveModalProps {
  campaign: Campaign;
  opened: boolean;
  close: () => void;
}

const menuPortalStyles: StylesConfig<ISelectOption, boolean, GroupBase<ISelectOption>> = {
  menuPortal: (base: CSSObjectWithLabel) => ({ ...base, zIndex: zIndex.popover }),
};

function CampaignMoveModal({
  campaign,
  opened,
  close,
}: Readonly<ICampaignMoveModalProps>): JSX.Element {
  const { t } = useTranslation();
  const history = useHistory();
  const localUrl = useLocalUrl();
  const { Campaigns } = useStore();
  const [selectedProject, setSelectedProject] = useState<ISelectOption>();
  const [isMoving, setIsMoving] = useState(false);

  const currentEventId = campaign.get('event');
  const confirmDisabled = !selectedProject || isMoving;

  // Reset selected project when modal closes
  useEffect(() => {
    if (!opened) {
      setSelectedProject(undefined);
    }
  }, [opened]);

  async function handleMove(): Promise<void> {
    if (!selectedProject) {
      return;
    }

    setIsMoving(true);
    try {
      await campaign.move(selectedProject.id);
      // Remove campaign from collection so it disappears from the current project's table
      Campaigns.remove(campaign.id);
      const projectUrl = localUrl(`/projects/${selectedProject.id}/campaigns/all`);
      toast(
        t("Moved '{{name}}' to {{projectName}}.", {
          name: campaign.name,
          projectName: selectedProject.name,
        }),
        {
          type: 'success',
          action: {
            label: t('View project'),
            onClick: () => history.push(projectUrl),
          },
        },
      );
      close();
    } catch (error: unknown) {
      showError(error, t);
    } finally {
      setIsMoving(false);
    }
  }

  function handleChangeProject(project: ISelectOption): void {
    setSelectedProject(project);
  }

  return (
    <Modal
      onClose={close}
      opened={opened}
      rightActions={
        <>
          <Button onClick={close}>{t('Cancel')}</Button>
          <Button
            disabled={confirmDisabled}
            isLoading={isMoving}
            onClick={handleMove}
            type={'primary'}
          >
            {t('Move')}
          </Button>
        </>
      }
      title={t('Move campaign: {{name}}', { name: campaign.name })}
    >
      <Stack>
        <Alert
          description={t(
            'Moving this campaign will affect the stats of the project, Flights that it is currently a part of and the project that it is moved to.',
          )}
          title={t('Are you sure you want to move this campaign?')}
          type={'warning'}
        />
        <EventSelect
          excludeId={currentEventId}
          label={t('Project')}
          menuPortalTarget={document.body}
          onChange={handleChangeProject}
          placeholder={t('Search or select a project')}
          selectStyles={menuPortalStyles}
          value={selectedProject}
        />
        <CampaignSummary campaign={campaign} layout={'vertical'} showMinimal={true} />
      </Stack>
    </Modal>
  );
}

export default observer(CampaignMoveModal);
